import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import ChatbotLayout from "../../chatbot/shared/ChatbotLayout";
import Message from "../../../i18n/Message";
import { CableIcon, PlusIcon } from "lucide-react";
import Spinner from "../../../assets/icons/Spinner";
import ZeroState from "../../chatbot/shared/ZeroState";

import "./McpServersPage.scss";
import CreateMcpServerDialog from "./CreateMcpServerDialog";
import McpServerTile from "./McpServerTile";
import { loadMcpPlaygrounds } from "../store/mcpPlaygroundActionCreators";
import { mcpPlaygroundSelectors } from "../store/mcpPlaygroundReducer";
import { subscriptionPlan } from "../../shared/pricingLimits";
import useSubscriptionGate from "../../shared/subscription/useSubscriptionGate";
import UpgradeHint from "../../shared/subscription/UpgradeHint";
import useOauthResultToast from "../oauth/useOauthResultToast";

const McpServersPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const playgrounds = useSelector(mcpPlaygroundSelectors.getPlaygrounds);
  const [showCreate, setShowCreate] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The playground whose analysis we are waiting on, carried in the URL rather than in state.
   *
   * Creating with OAuth hands the browser to the authorization server, which destroys this page;
   * the backend's callback returns it here with ?analyzing=<uid> (McpOAuthController#callback) and
   * the creation dialog picks the wait back up. Keeping it in the URL is also what makes a reload
   * resume and a navigation elsewhere stop — no marker to expire or clean up.
   */
  const analyzingUid = searchParams.get("analyzing");

  const setAnalyzingParam = (uid) => {
    const next = new URLSearchParams(searchParams);
    if (uid) {
      next.set("analyzing", uid);
    } else {
      next.delete("analyzing");
    }
    setSearchParams(next, { replace: true });
  };

  const closeCreateDialog = () => {
    setShowCreate(false);
    if (searchParams.has("analyzing")) setAnalyzingParam(null);
  };

  // An OAuth failure whose flow state no longer identifies a server lands here rather than on a
  // server's settings page. Until this was mounted, that error was silently invisible.
  useOauthResultToast();

  useEffect(() => {
    if (!playgrounds) {
      dispatch(loadMcpPlaygrounds(intl));
    }
  }, [playgrounds, intl, dispatch]);

  const { isReadOnly, plan, limit, isEnterprise } = useSubscriptionGate();
  // Effective playground cap: per-subscription override (server-resolved), else the plan default.
  const maxPlaygrounds = limit("playgrounds");
  const isLimitReached = maxPlaygrounds <= playgrounds?.length;
  const createDisabled = isReadOnly || isLimitReached;
  // Only Lite can self-serve upgrade (to Pro); Pro is the top self-serve tier.
  const canUpgradeForMore = plan === subscriptionPlan.LITE;

  return (
    <ChatbotLayout contentPanelClassName="mcp_servers_page">
      <div className="breadcrumbs">&nbsp;</div>
      <h1 className="servers_title">
        <Message id="mcp_servers_title" />
      </h1>
      <div className="actions_panel">
        <button
          className="btn btn-outline-dark btn-sm"
          onClick={() => setShowCreate(true)}
          disabled={createDisabled}
        >
          <PlusIcon size="18" color="currentColor" />
          <Message id="create_playground_button" />
        </button>
        {isReadOnly ? (
          <UpgradeHint messageId="upgrade_hint_readonly" plan={undefined} />
        ) : isLimitReached ? (
          canUpgradeForMore ? (
            <UpgradeHint messageId="upgrade_hint_feature" plan="pro" />
          ) : isEnterprise ? (
            // Enterprise hit their negotiated cap -> contact hint (no higher self-serve plan).
            <UpgradeHint messageId="playground_limit_reached" />
          ) : (
            <span className="playground_limit_note">
              <Message id="playground_limit_reached" />
            </span>
          )
        ) : null}
      </div>
      <div className="body_content">
        <div className="content_header">
          <div></div>
        </div>
        <div className="campaign_tiles mcp_tiles_list">
          {!playgrounds && <Spinner />}
          {playgrounds && playgrounds.length === 0 && (
            <div className="zero_state_container">
              <ZeroState
                icon={<CableIcon color="#67748e" size="36" />}
                textKey="chatbot_list_zero_state_text"
                buttonKey=""
              />
            </div>
          )}
          {playgrounds &&
            playgrounds.map((item) => (
              <McpServerTile key={item.uid} playground={item} />
            ))}
        </div>
      </div>
      {(showCreate || analyzingUid) && (
        <CreateMcpServerDialog
          analyzingUid={analyzingUid}
          onCreated={setAnalyzingParam}
          onClose={closeCreateDialog}
        />
      )}
    </ChatbotLayout>
  );
};

export default McpServersPage;
